import { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { fs, formulaMeta } from "./tokens";
import { DerivedRow, fmtBaht, fmtPct } from "./derive";
import FormulaChip from "./FormulaChip";
import {
  confirmFormula,
  rejectFormula,
  overrideFormula,
} from "../../services";
import {
  FormulaDecision,
  FormulaDecisionRequest,
} from "../../types/formula-scorer.type";

type ActionKind = "confirm" | "reject" | "override";

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 700, color: fs.dim, mb: 1 }}>
    {children}
  </Typography>
);

function MoneyLine({
  label,
  value,
  pct,
  color,
}: {
  label: React.ReactNode;
  value: number;
  pct?: number;
  color: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: fs.text }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>
        +฿{fmtBaht(value)}
        {pct != null && (
          <Typography component="span" sx={{ fontSize: 11, color: fs.faint, ml: 0.5 }}>
            (+{fmtPct(pct)}%)
          </Typography>
        )}
      </Typography>
    </Box>
  );
}

export default function RowDetail({
  row,
  onDecided,
  onReload,
}: {
  row: DerivedRow;
  // Confirm / reject: patch just this row. Re-scoring the route would take seconds.
  onDecided: (customerCode: string, decision: FormulaDecision) => void;
  // Override: the assignment moves to a formula that is not the winner, which changes
  // the whole row (current formula, recommendation, gain). Only a real run can say what
  // it becomes, so this one reloads.
  onReload: () => void;
}) {
  const { result } = row;
  const [busy, setBusy] = useState<ActionKind | null>(null);

  const otherFormula = result.scores.find(
    (s) => s.formula_code !== result.current_formula_code,
  )?.formula_code;

  const act = async (kind: ActionKind, formula_code: string | undefined) => {
    if (!formula_code) return;
    const body: FormulaDecisionRequest = {
      customer_code: result.customer_code,
      window_end: result.window_end,
      formula_code,
    };
    try {
      setBusy(kind);
      if (kind === "confirm") {
        await confirmFormula(body);
        onDecided(result.customer_code, "confirmed");
      } else if (kind === "reject") {
        await rejectFormula(body);
        onDecided(result.customer_code, "rejected");
      } else {
        await overrideFormula(body);
        onReload();
      }
    } finally {
      setBusy(null);
    }
  };

  const winnerCode = result.winner_formula_code;
  const winnerLabel = formulaMeta(winnerCode).label;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 2,
        p: 2,
        bgcolor: fs.surface2,
      }}
    >
      {/* LEFT: reasoning + money */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <SectionTitle>ทำไมถึงตัดสินแบบนี้</SectionTitle>
          <Typography sx={{ fontSize: 13, color: fs.text, lineHeight: 1.6 }}>
            {result.recommendation === "switch" && (
              <>
                ตลอด {row.coverageDays} วัน ({row.rounds} รอบ){" "}
                <b>{winnerLabel}</b> ขาดทุนน้อยกว่า{" "}
                <b>{formulaMeta(result.current_formula_code).label}</b> อยู่{" "}
                <b style={{ color: fs.pos }}>+{fmtPct(row.gainPct)}%</b> (≈ ฿
                {fmtBaht(row.gainPerRound)}/รอบ)
              </>
            )}
            {result.recommendation === "keep" && (
              <>
                สูตรที่ใช้อยู่ (<b>{formulaMeta(result.current_formula_code).label}</b>)
                ยังขาดทุนน้อยที่สุดจาก {row.rounds} รอบที่วัดได้ — ไม่ต้องเปลี่ยน
              </>
            )}
            {result.recommendation === "insufficient" && (
              <>รอบที่ประเมินได้ยังน้อยเกินไป ({row.rounds} รอบ) — รอข้อมูลเพิ่มก่อนตัดสิน</>
            )}
          </Typography>
        </Box>

        {result.recommendation === "switch" && (
          <Box
            sx={{
              border: `1px solid ${fs.line}`,
              borderRadius: 2,
              p: 1.5,
              bgcolor: fs.surface,
            }}
          >
            <SectionTitle>กำไรเพิ่มที่คาดว่าจะได้ / รอบ</SectionTitle>
            <MoneyLine
              label="ของเสียลดลง"
              value={row.wasteReductionPerRound}
              color={fs.pos}
            />
            <MoneyLine
              label="โอกาสขายที่ได้คืน"
              value={row.recoveryPerRound}
              color={fs.pos}
            />
            <Box sx={{ height: 1, bgcolor: fs.line, my: 0.5 }} />
            <MoneyLine
              label={<b>รวมกำไรเพิ่ม</b>}
              value={row.gainPerRound}
              pct={row.gainPct}
              color={fs.pos}
            />
            {row.isLowMoney && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: fs.warnBg,
                  display: "flex",
                  gap: 0.75,
                  alignItems: "flex-start",
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 16, color: fs.warn, mt: "1px" }} />
                <Typography sx={{ fontSize: 11.5, color: fs.warn, lineHeight: 1.5 }}>
                  <b>ระวัง:</b> เปอร์เซ็นต์สูง (+{fmtPct(row.gainPct)}%) แต่เม็ดเงินจริงเพียง{" "}
                  <b>฿{fmtBaht(row.gainPerRound)}/รอบ</b> — พิจารณาว่าคุ้มค่าเปลี่ยนหรือไม่
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* RIGHT: timeline + actions + products */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box>
          <SectionTitle>ประวัติสูตรที่เคยใช้</SectionTitle>
          {result.history.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: fs.faint }}>
              ยังไม่เคยเปลี่ยนสูตร (ใช้ค่าเริ่มต้น)
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {result.history.map((h, i) => (
                <Box
                  key={`${h.formula_code}-${h.effective_from}-${i}`}
                  sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12 }}
                >
                  <FormulaChip code={h.formula_code} size={12} />
                  <Typography sx={{ fontSize: 11, color: fs.faint }}>
                    {dayjs(h.effective_from).format("D MMM YY")}
                    {h.effective_to ? ` – ${dayjs(h.effective_to).format("D MMM YY")}` : " – ปัจจุบัน"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box>
          {result.recommendation === "switch" && result.decision === "pending" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontSize: 12.5, color: fs.text }}>
                ระบบแนะนำให้ <b>สลับเป็น {winnerLabel}</b> — ยืนยันเพื่อให้มีผลรอบถัดไป
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={busy === "confirm" ? undefined : <CheckIcon />}
                  disabled={busy != null}
                  onClick={() => act("confirm", winnerCode ?? undefined)}
                  sx={{ bgcolor: fs.pos, "&:hover": { bgcolor: "#0e7a4a" } }}
                >
                  {busy === "confirm" ? <CircularProgress size={16} color="inherit" /> : "ยืนยันสลับสูตร"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  disabled={busy != null}
                  onClick={() => act("reject", winnerCode ?? undefined)}
                  sx={{ color: fs.dim, borderColor: fs.line }}
                >
                  ไม่สลับ (คงเดิม)
                </Button>
              </Box>
            </Box>
          )}

          {result.recommendation === "switch" && result.decision === "confirmed" && (
            <ActedBanner
              color={fs.pos}
              bg={fs.posBg}
              text={`ยืนยันแล้ว — สลับเป็น ${winnerLabel}`}
            />
          )}
          {result.recommendation === "switch" && result.decision === "rejected" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <ActedBanner
                color={fs.dim}
                bg={fs.mutedBg}
                text={`ปฏิเสธแล้ว — คงสูตรเดิม (${formulaMeta(result.current_formula_code).label})`}
              />
              <Button
                size="small"
                variant="text"
                disabled={busy != null}
                onClick={() => act("confirm", winnerCode ?? undefined)}
                sx={{ alignSelf: "flex-start", color: fs.keep }}
              >
                {busy === "confirm" ? <CircularProgress size={16} /> : "เปลี่ยนใจ — สลับสูตร"}
              </Button>
            </Box>
          )}

          {result.recommendation === "keep" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontSize: 12.5, color: fs.text }}>
                ระบบแนะนำให้ <b>คงสูตรเดิม</b> — ไม่ต้องทำอะไรเพิ่ม
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={busy != null || !otherFormula}
                onClick={() => act("override", otherFormula)}
                sx={{ alignSelf: "flex-start", color: fs.dim, borderColor: fs.line }}
              >
                {busy === "override" ? (
                  <CircularProgress size={16} />
                ) : (
                  `สลับด้วยตนเองเป็น ${formulaMeta(otherFormula).label} (override)`
                )}
              </Button>
            </Box>
          )}

          {result.recommendation === "insufficient" && (
            <ActedBanner
              color={fs.muted}
              bg={fs.mutedBg}
              text="ข้อมูลไม่พอ — ยังตัดสินใจไม่ได้"
            />
          )}
        </Box>

        {/* Product-level (view only) */}
        {result.products.length > 0 && (
          <Box>
            <SectionTitle>
              ระดับสินค้า{" "}
              <Typography component="span" sx={{ fontSize: 10, color: fs.faint, fontWeight: 400 }}>
                — ดูอย่างเดียว ประกอบการพิจารณา
              </Typography>
            </SectionTitle>
            <ProductTable row={row} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function ActedBanner({ color, bg, text }: { color: string; bg: string; text: string }) {
  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        bgcolor: bg,
        color,
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      {text}
    </Box>
  );
}

function ProductTable({ row }: { row: DerivedRow }) {
  const winnerCode = row.result.winner_formula_code ?? row.result.current_formula_code;
  const cellSx = { fontSize: 11.5, py: 0.5, borderColor: fs.line } as const;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 340 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cellSx, color: fs.dim }}>สินค้า</TableCell>
            <TableCell align="right" sx={{ ...cellSx, color: fs.dim }}>พยากรณ์</TableCell>
            <TableCell align="right" sx={{ ...cellSx, color: fs.dim }}>ขายจริง</TableCell>
            <TableCell align="right" sx={{ ...cellSx, color: fs.dim }}>คลาดเคลื่อน</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {row.result.products.slice(0, 12).map((p) => {
            const bf = p.by_formula.find((f) => f.formula_code === winnerCode);
            const forecast = bf?.forecast ?? 0;
            const actual = bf?.actual ?? 0;
            const err = Math.round((forecast - actual) * 10) / 10;
            return (
              <TableRow key={p.product_code}>
                <TableCell sx={{ ...cellSx, color: fs.text }}>{p.product_code}</TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: fs.text }}>
                  {Math.round(forecast)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellSx, color: fs.text }}>
                  {actual}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ ...cellSx, color: err === 0 ? fs.faint : err > 0 ? fs.warn : fs.crit }}
                >
                  {err > 0 ? `+${err}` : err}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
